import Head from 'next/head';
import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { Button, Grid, GridItem } from '@chakra-ui/react';

import {
    CUSTOM_IMAGE_SIZES,
    CustomImageSize,
    IMAGE_SIZES,
    imageHelpers,
    ImageRatio,
    ImageSize,
} from '@nocowanie/core';
import { Image, usePluralisationTranslation } from '@nocowanie/common-ui';

import { GalleryGridProps } from './gallery-grid.props';

export const GalleryGrid = ({ images, name, onItemClick }: GalleryGridProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'gallery',
    });

    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'gallery');

    const gridImages = useMemo(
        () =>
            images?.slice(0, 5).map((image, index) => {
                const imageSrc = imageHelpers.wpresizer(
                    index > 0
                        ? [
                              IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Xs].width,
                              IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Xs].width,
                          ]
                        : [
                              CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationMain].width,
                              CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationMain].height,
                          ],
                    image.url,
                );

                return (
                    <GridItem
                        onClick={onItemClick}
                        key={image.id}
                        rowSpan={index > 0 ? 'auto' : 2}
                        colSpan={index > 0 ? 2 : 7}
                        display={{ base: index > 0 ? 'none' : 'block', sm: 'block' }}
                        cursor={'pointer'}
                        _hover={{
                            opacity: 0.75,
                        }}
                        transition={'opacity'}
                    >
                        <Head>
                            <link
                                rel="preload"
                                as="image"
                                href={imageSrc}
                                type="image/webp"
                                {...({
                                    fetchpriority: 'high',
                                } as React.HTMLAttributes<HTMLLinkElement>)}
                            />
                        </Head>

                        <Image
                            htmlWidth={
                                index > 0
                                    ? IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Xs].width
                                    : CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationMain].width
                            }
                            htmlHeight={
                                index > 0
                                    ? IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Xs].width // for now, we don't have predefined sizes for ratio 1/1, so 'width' property from another ratio is used here
                                    : CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationMain].height
                            }
                            src={imageSrc}
                            ratioProps={{
                                ratio: index > 0 ? 1 : ImageRatio['16/9'],
                            }}
                            objectFit="cover"
                            loading={index > 0 ? 'lazy' : undefined}
                            fetchpriority={index > 0 ? undefined : 'high'}
                            borderRadius={'sm'}
                            alt={`${name ? name : t('altTextPlaceholder')} (${index + 1})`}
                            data-testid="gallery-grid-image"
                        />
                    </GridItem>
                );
            }),
        [images],
    );

    return (
        <Grid
            pos="relative"
            templateRows="repeat(2, 1fr)"
            templateColumns={{ base: 'auto', sm: 'repeat(11, 1fr)' }}
            gap={2}
            data-testid="gallery-grid"
        >
            {gridImages.length ? (
                <>
                    {gridImages}
                    <Button
                        onClick={onItemClick}
                        size="xs"
                        pos="absolute"
                        bottom="3"
                        left="3"
                        px={3}
                        py={4}
                        color={'white'}
                        border={'none'}
                        fontWeight={'normal'}
                        bgColor={'blackAlpha.600'}
                        _hover={{ bg: 'blackAlpha.600' }}
                        data-testid="gallery-grid-images-count"
                    >
                        {t('showAll')} {pluralizeTranslation(images.length, 'photo')}
                    </Button>
                </>
            ) : null}
        </Grid>
    );
};
